import { ContextMenu as BaseContextMenu } from "@base-ui/react/context-menu";
import { cn } from "./cn";

const popupClasses =
    "min-w-44 rounded-md border border-border bg-surface p-1 shadow-lg outline-none " +
    "transition-all duration-100 origin-[var(--transform-origin)] " +
    "data-[starting-style]:scale-95 data-[starting-style]:opacity-0 " +
    "data-[ending-style]:scale-95 data-[ending-style]:opacity-0";

const itemClasses =
    "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none " +
    "data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg " +
    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed";

/**
 * Right-click context menu (Base UI). Positions at the pointer automatically.
 * Compose as:
 *
 *   <ContextMenu.Root>
 *     <ContextMenu.Trigger render={<span>…</span>} />
 *     <ContextMenu.Content>
 *       <ContextMenu.GroupLabel>Header</ContextMenu.GroupLabel>
 *       <ContextMenu.Item onClick={…}>Copy</ContextMenu.Item>
 *     </ContextMenu.Content>
 *   </ContextMenu.Root>
 */
export const ContextMenu = {
    Root: BaseContextMenu.Root,
    Trigger: BaseContextMenu.Trigger,
    Group: BaseContextMenu.Group,
    GroupLabel: function GroupLabel({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseContextMenu.GroupLabel>) {
        return (
            <BaseContextMenu.GroupLabel
                className={cn("px-2 py-1 text-xs font-medium text-muted", className as string)}
                {...props}
            />
        );
    },
    Item: function Item({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseContextMenu.Item>) {
        return <BaseContextMenu.Item className={cn(itemClasses, className as string)} {...props} />;
    },
    Separator: function Separator({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseContextMenu.Separator>) {
        return (
            <BaseContextMenu.Separator
                className={cn("-mx-1 my-1 h-px bg-border", className as string)}
                {...props}
            />
        );
    },
    /** Portal + positioner + popup in one convenient wrapper. */
    Content: function Content({
        className,
        children,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseContextMenu.Positioner>) {
        return (
            <BaseContextMenu.Portal>
                {/* z-index on the Positioner (the positioned element), not the Popup. */}
                <BaseContextMenu.Positioner className="z-[1100]" {...props}>
                    <BaseContextMenu.Popup className={cn(popupClasses, className as string)}>
                        {children}
                    </BaseContextMenu.Popup>
                </BaseContextMenu.Positioner>
            </BaseContextMenu.Portal>
        );
    },
};
